import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { FaCalendarAlt, FaLock, FaUnlock } from "react-icons/fa";
import "../styles/DashboardMedecin.css";

import { API_BASE } from "../config";

import { DisponibilitesMedecin } from "./PageDisponibilite.js";
import { BlocagesMedecin } from "./PageBlocage.js";
import { MonCabinetMedecin } from "./PageModif_InfosCabinet.js";
import { RendezVousMedecin } from "./PageRDV.js";

/* --- Inline SVG icons (no external libs) --- */
const Icons = {
  overview: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-18v6h8V3h-8z" />
    </svg>
  ),
  availability: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-2V2h-2v2H9V2H7zm12 18H5V9h14v11z" />
      <path d="M8 12h3v3H8zM13 12h3v3h-3z" />
    </svg>
  ),
  blocks: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm6.93 10a6.9 6.9 0 0 1-1.06 3.65L8.35 6.13A6.9 6.9 0 0 1 12 5.07 6.93 6.93 0 0 1 18.93 12zM5.07 12a6.9 6.9 0 0 1 1.06-3.65l9.52 9.52A6.9 6.9 0 0 1 12 18.93 6.93 6.93 0 0 1 5.07 12z" />
    </svg>
  ),
  rdv: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
      <path d="M7 11h4v4H7z" />
    </svg>
  ),
  cabinet: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 6a3 3 0 1 0-3-3 3 3 0 0 0 3 3z" />
      <path d="M4 22v-1c0-3.31 2.69-6 6-6h4c3.31 0 6 2.69 6 6v1H4z" />
      <path d="M16 10h5v2h-5zM18 8h2v6h-2z" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 17l1.41-1.41L8.83 13H20v-2H8.83l2.58-2.59L10 7l-5 5 5 5zM4 4h6V2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h6v-2H4V4z" />
    </svg>
  ),
};

const EMPTY_STATS = {
  today: {
    rdv: 0,
    creneauxDispo: 0,
    blocages: 0,
    confirmes: 0,
  },
  week: {
    rdv: 0,
    creneauxDispo: 0,
    blocages: 0,
    patients: 0,
  },
};

/* ===========================
   DASHBOARD
=========================== */
const DashboardMedecin = ({ user, onLogout }) => {
  const menuItems = useMemo(
    () => [
      { key: "overview", label: "Vue globale", icon: Icons.overview },
      { key: "disponibilites", label: "Mes disponibilités", icon: Icons.availability },
      { key: "blocages", label: "Mes absences", icon: Icons.blocks },
      { key: "rendezvous", label: "Mes rendez-vous", icon: Icons.rdv },
      { key: "cabinet", label: "Mon cabinet", icon: Icons.cabinet },
    ],
    []
  );

  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  }

  const [confirmLogout, setConfirmLogout] = useState(false);
  const [active, setActive] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [stats, setStats] = useState(EMPTY_STATS);
  const [, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");

  const requestInFlightRef = useRef(false);
  const lastPayloadRef = useRef("");

  const fetchDashboardStats = useCallback(async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;

    try {
      const token = localStorage.getItem("access_token");

      if (!token) {
        setStatsError("Session expirée. Veuillez vous reconnecter.");
        setStats(EMPTY_STATS);
        return;
      }

      setStatsLoading(true);
      setStatsError("");

      const res = await fetch(`${API_BASE}/medecin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("dashboard error", res.status, txt);

        if (res.status === 401) {
          setStatsError("Non autorisé. Veuillez vous reconnecter.");
        } else if (res.status === 403) {
          setStatsError("Accès refusé.");
        } else {
          setStatsError("Impossible de charger les statistiques.");
        }
        return;
      }

      const data = await res.json();

      const nextStats = {
        today: {
          rdv: data?.today?.rdv ?? 0,
          creneauxDispo: data?.today?.creneauxDispo ?? 0,
          blocages: data?.today?.blocages ?? 0,
          confirmes: data?.today?.confirmes ?? 0,
        },
        week: {
          rdv: data?.week?.rdv ?? 0,
          creneauxDispo: data?.week?.creneauxDispo ?? 0,
          blocages: data?.week?.blocages ?? 0,
          patients: data?.week?.patients ?? 0,
        },
      };

      const nextPayload = JSON.stringify(nextStats);
      if (nextPayload !== lastPayloadRef.current) {
        lastPayloadRef.current = nextPayload;
        setStats(nextStats);
      }
    } catch (err) {
      console.error("Erreur dashboard:", err);
      setStatsError("Erreur réseau lors du chargement des statistiques.");
    } finally {
      setStatsLoading(false);
      requestInFlightRef.current = false;
    }
  }, []);

  // Charge les stats quand on ouvre la vue globale
  useEffect(() => {
    if (active === "overview") {
      fetchDashboardStats();
    }
  }, [active, fetchDashboardStats]);

  // Rafraîchissement auto pendant la vue globale (toutes les 20s)
  useEffect(() => {
    if (active !== "overview") return;

    const id = setInterval(() => {
      fetchDashboardStats();
    }, 20000);

    return () => clearInterval(id);
  }, [active, fetchDashboardStats]);

  // Rafraîchit aussi quand l’onglet redevient visible
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && active === "overview") {
        fetchDashboardStats();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [active, fetchDashboardStats]);

  const StatBox = ({ label, value, trend }) => {
    return (
      <div className="statCard">
        <div className="statTrend">{trend}</div>
        <div className="statLabel">{label}</div>
        <div className="statValue">{value}</div>
      </div>
    );
  };

  const pageTitle = (k) => menuItems.find((i) => i.key === k)?.label || "Espace Médecin";
  const closeSidebar = () => setSidebarOpen(false);

  const handleSelect = (k) => {
    setActive(k);
    closeSidebar();
  };

  return (
    <div className="adminApp">
      <button
        className={`adminOverlay ${sidebarOpen ? "isOpen" : ""}`}
        aria-label="Fermer le menu"
        onClick={closeSidebar}
      />

      <aside className={`adminSidebar ${sidebarOpen ? "isOpen" : ""}`}>
        <div className="adminBrand">
          <div className="adminLogo">FD</div>
          <div className="adminBrandText">
            <div className="adminBrandTitle">FormiDoc</div>
            <div className="adminBrandSub">Espace Médecin</div>
          </div>
        </div>

        <div className="adminProfile">
          <div className="adminProfileTop">
            <div className="adminProfileLabel">Connecté en tant que</div>
            <span className="adminRolePill">Médecin</span>
          </div>
          <div className="adminEmail">{user?.email}</div>
        </div>

        <nav className="adminNav">
          {menuItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`adminNavItem ${active === item.key ? "active" : ""}`}
              onClick={() => handleSelect(item.key)}
            >
              <span className="adminNavIcon">{item.icon}</span>
              <span className="adminNavText">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="adminSidebarFooter">
          <button className="adminLogout" onClick={() => setConfirmLogout(true)}>
            <span className="adminLogoutIcon">{Icons.logout}</span>
            <span>Se déconnecter</span>
          </button>
        </div>
      </aside>

      {confirmLogout && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <div className="logoutModalTitle">Confirmation</div>
            <div className="logoutModalText">Voulez-vous vraiment vous déconnecter ?</div>

            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setConfirmLogout(false)}>
                Annuler
              </button>

              <button
                className="btnDanger"
                onClick={() => {
                  setConfirmLogout(false);
                  localStorage.removeItem("access_token");
                  localStorage.removeItem("user_role");
                  localStorage.removeItem("user_email");
                  onLogout?.();
                }}
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="adminMain">
        <header className="adminTopbar">
          <div className="adminTopbarLeft">
            <button className="adminBurger" aria-label="Ouvrir le menu" onClick={() => setSidebarOpen(true)}>
              <span />
              <span />
              <span />
            </button>

            <div>
              <div className="adminTitle">{pageTitle(active)}</div>
              <div className="adminSubtitle"></div>
            </div>
          </div>
          <div className="adminTopbarRight">
            <button
              type="button"
              className="adminYsdaBtn"
              onClick={() => window.open("https://ysda.fr", "_blank")}
            >
              Aller sur Ysda.fr
            </button>
          </div>
        </header>

        <section className="adminContent">
          {active === "overview" && (
            <div className="dashboard-overview">
              <section className="dashSection">
                <div className="dashSectionHeader">
                  <h2>ACTIONS RAPIDES</h2>
                  <div className="line" />
                  <button
                    type="button"
                    className="badgeDate"
                    onClick={fetchDashboardStats}
                    style={{ cursor: "pointer", border: "none" }}
                    title="Rafraîchir les statistiques"
                  >
                    Rafraîchir
                  </button>
                </div>

                <div className="quickGrid">
                  <button className="quickCard primary" onClick={() => handleSelect("rendezvous")}>
                    <FaCalendarAlt />
                    <div>
                      <div className="quickTitle">Prise de rendez-vous</div>
                      <div className="quickSub">Ajouter un RDV pour un patient</div>
                    </div>
                  </button>

                  <button className="quickCard accent" onClick={() => handleSelect("disponibilites")}>
                    <FaUnlock />
                    <div>
                      <div className="quickTitle">Gérer les disponibilités</div>
                      <div className="quickSub">Configurer vos créneaux horaires</div>
                    </div>
                  </button>

                  <button className="quickCard warning" onClick={() => handleSelect("blocages")}>
                    <FaLock />
                    <div>
                      <div className="quickTitle">Gestion des Absences</div>
                      <div className="quickSub">Bloquer ou débloquer des créneaux</div>
                    </div>
                  </button>
                </div>
              </section>

              {statsError ? (
                <div className="adminPanel" style={{ marginBottom: 16 }}>
                  <div className="adminPanelHeader">
                    <div>
                      <div className="adminPanelTitle">Statistiques</div>
                      <div className="adminPanelSub">{statsError}</div>
                    </div>
                  </div>
                </div>
              ) : null}

              <section className="dashSection">
                <div className="dashSectionHeader">
                  <h2>AUJOURD'HUI</h2>
                  <div className="line" />
                  <span className="badgeDate">
                    {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                  </span>
                </div>

                <div className="statsGrid">
                  <StatBox label="Rendez-vous" value={stats.today.rdv} trend="" />
                  <StatBox label="Créneaux disponibles" value={stats.today.creneauxDispo} trend="" />
                  <StatBox label="Absences actifs" value={stats.today.blocages} trend="" />
                  <StatBox label="Confirmés" value={stats.today.confirmes} trend="" />
                </div>
              </section>

              <section className="dashSection">
                <div className="dashSectionHeader">
                  <h2>SEMAINE EN COURS</h2>
                  <div className="line" />
                  <span className="badgeDate">Semaine {getWeekNumber(new Date())}</span>
                </div>

                <div className="statsGrid">
                  <StatBox label="Rendez-vous" value={stats.week.rdv} trend="" />
                  <StatBox label="Créneaux disponibles" value={stats.week.creneauxDispo} trend="" />
                  <StatBox label="Absences" value={stats.week.blocages} trend="" />
                  <StatBox label="Patients vus" value={stats.week.patients} trend="" />
                </div>
              </section>
            </div>
          )}

          {active === "disponibilites" && <DisponibilitesMedecin user={user} />}
          {active === "cabinet" && <MonCabinetMedecin user={user} />}
          {active === "blocages" && <BlocagesMedecin user={user} />}
          {active === "rendezvous" && <RendezVousMedecin user={user} />}

          {active !== "overview" &&
            active !== "disponibilites" &&
            active !== "cabinet" &&
            active !== "rendezvous" &&
            active !== "blocages" && (
              <div className="adminPanel">
                <div className="adminPanelHeader">
                  <div>
                    <div className="adminPanelTitle">{pageTitle(active)}</div>
                    <div className="adminPanelSub">(Placeholder) — on branchera le backend ensuite.</div>
                  </div>
                </div>

                <div className="adminPlaceholder">
                  <div className="adminPlaceholderTitle">Placeholder</div>
                  <ul className="adminList">
                    <li>Écran à venir</li>
                  </ul>
                </div>
              </div>
            )}
        </section>
      </main>
    </div>
  );
};

export default DashboardMedecin;